import React, { useEffect, useState } from 'react';
import { Search, Users, Building2, Eye, Download, ChevronUp, ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '../config/supabase';
import { usePermissions } from '../hooks/usePermissions';
import { learnersService } from '../services/learners';
import { organizationsService } from '../services/organizations';
import { enrollmentsService } from '../services/enrollments';
import { getLearnerTrackingMap } from '../services/learnerTracking';
import { LearnerWithDetails, Organization, EnrollmentWithProgress } from '../types';
import { AccountBadge, TrackingBadge } from '../components/LearnerBadges';

interface LearnerAlert { type: 'delayed' | 'inactive'; daysSince: number }

const MINT  = '#2f9e5a';
const GOLD  = '#f39200';
const CORAL = '#af3216';

const DEFAULT_DELAY_DAYS = 14;
const DEFAULT_INACTIVE_DAYS = 21;
const getDelayThreshold = () => parseInt(localStorage.getItem('dlm_delay_threshold') || String(DEFAULT_DELAY_DAYS));
const getInactiveThreshold = () => parseInt(localStorage.getItem('dlm_inactive_threshold') || String(DEFAULT_INACTIVE_DAYS));

type SortColumn = 'fullname' | 'group' | 'status' | 'progress' | 'enrolledAt';
type SortDirection = 'asc' | 'desc';

interface InlineEnrollment {
  id: string;
  courseName: string;
  progressPercent: number;
  status: string;
  enrolledAt: string | null;
  completedAt: string | null;
  alertType?: 'delayed' | 'inactive';
  alertDays?: number;
}

export const Learners: React.FC = () => {
  const { isSuperAdmin, getUserOrganizationId } = usePermissions();

  const [learners, setLearners] = useState<LearnerWithDetails[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgMaxLearners, setSelectedOrgMaxLearners] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [accountFilter, setAccountFilter] = useState<string>('');
  const [trackingFilter, setTrackingFilter] = useState<string>('');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [viewingLearner, setViewingLearner] = useState<LearnerWithDetails | null>(null);
  const [learnerEnrollments, setLearnerEnrollments] = useState<EnrollmentWithProgress[]>([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);

  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const [expandedLearnerId, setExpandedLearnerId] = useState<string | null>(null);
  const [expandedEnrollments, setExpandedEnrollments] = useState<InlineEnrollment[]>([]);
  const [expandedLoading, setExpandedLoading] = useState(false);

  const [learnerAlertMap, setLearnerAlertMap] = useState<Map<string, LearnerAlert>>(new Map());
  const [completionMap, setCompletionMap] = useState<Map<string, { completed: number; total: number }>>(new Map());

  useEffect(() => { loadInitialData(); }, []);
  useEffect(() => { if (selectedOrgId) loadLearners(); }, [selectedOrgId]);

  const loadInitialData = async () => {
    try {
      setError(null);
      if (isSuperAdmin) {
        const orgs = await organizationsService.getAll();
        setOrganizations(orgs);
        if (orgs.length > 0) setSelectedOrgId(orgs[0].id);
      } else {
        const orgId = getUserOrganizationId();
        if (orgId) {
          setSelectedOrgId(orgId);
          const { data: orgData } = await supabase.from('organizations').select('max_learners').eq('id', orgId).single();
          setSelectedOrgMaxLearners(orgData?.max_learners ?? null);
        } else setError('Aucune organisation associee a votre compte');
      }
    } catch {
      setError('Erreur lors du chargement des donnees');
    } finally {
      setLoading(false);
    }
  };

  const loadLearners = async () => {
    if (!selectedOrgId) return;
    try {
      setError(null);
      const data = await learnersService.getByOrganization(selectedOrgId);
      setLearners(data);

      const selectedOrg = organizations.find(o => o.id === selectedOrgId);
      setSelectedOrgMaxLearners(selectedOrg?.maxLearners ?? null);

      // Suivi « vue d'ensemble » : meme logique partagee que le tableau de bord
      // (agrege TOUTES les formations de l'apprenant — voir services/learnerTracking).
      const tracking = await getLearnerTrackingMap(data.map(l => l.id));
      const alertMap = new Map<string, LearnerAlert>();
      const completion = new Map<string, { completed: number; total: number }>();
      tracking.forEach((t, id) => {
        if (t.alert) alertMap.set(id, t.alert);
        completion.set(id, { completed: t.completed, total: t.total });
      });

      setLearnerAlertMap(alertMap);
      setCompletionMap(completion);
    } catch {
      setError('Erreur lors du chargement des apprenants');
    }
  };

  const handleViewLearner = async (learner: LearnerWithDetails) => {
    setViewingLearner(learner);
    setLoadingEnrollments(true);
    try {
      const enrollments = await enrollmentsService.getByLearner(learner.id);
      setLearnerEnrollments(enrollments);
    } catch {
      setLearnerEnrollments([]);
    } finally {
      setLoadingEnrollments(false);
    }
  };

  const toggleExpand = async (learnerId: string) => {
    if (expandedLearnerId === learnerId) { setExpandedLearnerId(null); return; }
    setExpandedLearnerId(learnerId);
    setExpandedLoading(true);
    setExpandedEnrollments([]);
    try {
      const { data, error } = await supabase
        .from('enrollments')
        .select('id, enrolled_at, course_id, courses!inner(name), course_progress(progress_percent, completed_at, last_activity_at)')
        .eq('learner_id', learnerId);

      if (error) throw error;

      const STATUS_SORT: Record<string, number> = { completed: 0, in_progress: 1, not_started: 2 };
      const nowMs = Date.now();
      const delayMs = getDelayThreshold() * 86400000;
      const inactiveMs = getInactiveThreshold() * 86400000;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mapped: InlineEnrollment[] = ((data || []) as any[]).map((e) => {
        const course = Array.isArray(e.courses) ? e.courses[0] : e.courses;
        const cp = Array.isArray(e.course_progress) ? e.course_progress[0] : e.course_progress;
        const progressPercent: number = cp?.progress_percent ?? 0;
        const completedAt: string | null = cp?.completed_at ?? null;

        let status = 'not_started';
        if (completedAt || progressPercent >= 100) status = 'completed';
        else if (progressPercent > 0) status = 'in_progress';

        let alertType: 'delayed' | 'inactive' | undefined;
        let alertDays: number | undefined;
        if (status === 'in_progress' && cp?.last_activity_at) {
          const elapsed = nowMs - new Date(cp.last_activity_at).getTime();
          if (elapsed > delayMs) { alertType = 'delayed'; alertDays = Math.floor(elapsed / 86400000); }
        } else if (status === 'not_started' && e.enrolled_at) {
          const elapsed = nowMs - new Date(e.enrolled_at).getTime();
          if (elapsed > inactiveMs) { alertType = 'inactive'; alertDays = Math.floor(elapsed / 86400000); }
        }

        return { id: e.id, courseName: course?.name || 'Inconnu', progressPercent, status, enrolledAt: e.enrolled_at, completedAt, alertType, alertDays };
      });

      mapped.sort((a, b) => {
        const sc = (STATUS_SORT[a.status] ?? 2) - (STATUS_SORT[b.status] ?? 2);
        if (sc !== 0) return sc;
        return b.progressPercent - a.progressPercent;
      });

      setExpandedEnrollments(mapped);
    } catch (err) {
      console.error('Error loading learner enrollments:', err);
      setExpandedEnrollments([]);
    } finally {
      setExpandedLoading(false);
    }
  };

  const accountLabel = (status: string) =>
    status === 'inactive' ? 'Désactivé' : 'Actif';

  const trackingLabel = (l: LearnerWithDetails) => {
    if (l.status === 'inactive') return '—';
    const a = learnerAlertMap.get(l.id);
    if (!a) return 'En formation';
    return a.type === 'inactive'
      ? `Pas démarré depuis ${a.daysSince} j`
      : `En retard depuis ${a.daysSince} j`;
  };

  const handleExportCSV = () => {
    const headers = ['Nom', 'Email', 'Groupe', 'Suivi', 'Compte', 'Progression globale', 'Formations terminées', 'Date inscription'];
    const rows = filteredLearners.map(l => {
      const c = completionMap.get(l.id);
      return [
        l.fullname, l.email,
        l.groups?.map(g => g.name).join('; ') || '',
        trackingLabel(l), accountLabel(l.status), `${l.overallProgress}%`,
        `${c?.completed ?? 0}/${c?.total ?? 0}`,
        l.createdAt ? new Date(l.createdAt).toLocaleDateString('fr-FR') : '',
      ];
    });
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `apprenants_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDirection('asc'); }
  };

  const filteredLearners = learners
    .filter(l => {
      const matchesSearch = !searchQuery ||
        l.fullname.toLowerCase().includes(searchQuery.toLowerCase()) ||
        l.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesAccount = !accountFilter || l.status === accountFilter;
      const alert = learnerAlertMap.get(l.id);
      let matchesTracking = true;
      if (trackingFilter === 'in_training') matchesTracking = l.status !== 'inactive' && !alert;
      else if (trackingFilter === 'delayed') matchesTracking = alert?.type === 'delayed';
      else if (trackingFilter === 'inactive') matchesTracking = alert?.type === 'inactive';
      return matchesSearch && matchesAccount && matchesTracking;
    })
    .sort((a, b) => {
      if (!sortColumn) return 0;
      const dir = sortDirection === 'asc' ? 1 : -1;
      switch (sortColumn) {
        case 'fullname': return dir * a.fullname.localeCompare(b.fullname);
        case 'group': return dir * (a.groups?.[0]?.name || '').localeCompare(b.groups?.[0]?.name || '');
        case 'status': return dir * a.status.localeCompare(b.status);
        case 'progress': return dir * (a.overallProgress - b.overallProgress);
        case 'enrolledAt': {
          const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dir * (aTime - bTime);
        }
        default: return 0;
      }
    });

  const SortIcon: React.FC<{ column: SortColumn }> = ({ column }) => {
    if (sortColumn !== column) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortDirection === 'asc'
      ? <ChevronUp className="w-3 h-3 text-coral" />
      : <ChevronDown className="w-3 h-3 text-coral" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">Apprenants</h1>
          <p className="text-ink-soft mt-1">
            {filteredLearners.length} apprenant{filteredLearners.length > 1 ? 's' : ''}
            {selectedOrgMaxLearners !== null && (
              <span className={`ml-2 inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                learners.length >= selectedOrgMaxLearners ? 'bg-primary-container text-coral' :
                learners.length >= selectedOrgMaxLearners * 0.8 ? 'bg-gold-container text-gold' :
                'bg-mint-container text-mint'
              }`}>
                {learners.length} / {selectedOrgMaxLearners}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={handleExportCSV}
          className="border border-coral/30 text-coral hover:bg-coral/5 rounded-full px-4 py-2 text-sm font-medium transition-all duration-300 flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          Exporter CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-ink-soft" />
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="bg-white border border-cream-high rounded-xl text-ink text-sm focus:outline-none focus:ring-2 focus:ring-coral/20 px-3 py-2 w-auto"
            >
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-soft" />
          <input
            type="text"
            placeholder="Rechercher un apprenant..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-transparent border border-cream-high rounded-xl text-ink placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral/30 pl-10 py-2 pr-4"
          />
        </div>
        <select
          value={trackingFilter}
          onChange={(e) => setTrackingFilter(e.target.value)}
          className="bg-white border border-cream-high rounded-xl text-ink text-sm focus:outline-none focus:ring-2 focus:ring-coral/20 px-3 py-2 w-auto"
          title="Filtrer par suivi"
        >
          <option value="">Tous les suivis</option>
          <option value="in_training">En formation</option>
          <option value="delayed">En retard</option>
          <option value="inactive">Pas démarré</option>
        </select>
        <select
          value={accountFilter}
          onChange={(e) => setAccountFilter(e.target.value)}
          className="bg-white border border-cream-high rounded-xl text-ink text-sm focus:outline-none focus:ring-2 focus:ring-coral/20 px-3 py-2 w-auto"
          title="Filtrer par compte"
        >
          <option value="">Tous les comptes</option>
          <option value="active">Actif</option>
          <option value="inactive">Désactivé</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm">{error}</div>
      )}

      {/* Learners Table */}
      <div className="bg-white shadow-ambient rounded-2xl overflow-hidden">
        <div>
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-[22%]" />
              <col className="w-[12%]" />
              <col className="w-[16%]" />
              <col className="w-[10%]" />
              <col className="w-[14%]" />
              <col className="w-[10%]" />
              <col className="w-[10%]" />
              <col className="w-[6%]" />
            </colgroup>
            <thead className="bg-cream-high/30">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em] cursor-pointer" onClick={() => handleSort('fullname')}>
                  <span className="flex items-center gap-1">Apprenant <SortIcon column="fullname" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em] cursor-pointer" onClick={() => handleSort('group')}>
                  <span className="flex items-center gap-1">Groupe <SortIcon column="group" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">
                  <span className="flex items-center gap-1">Suivi</span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em] cursor-pointer" onClick={() => handleSort('status')}>
                  <span className="flex items-center gap-1">Compte <SortIcon column="status" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em] cursor-pointer" onClick={() => handleSort('progress')}>
                  <span className="flex items-center gap-1">Progression globale <SortIcon column="progress" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">
                  <span className="flex items-center gap-1">Formations terminées</span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em] cursor-pointer" onClick={() => handleSort('enrolledAt')}>
                  <span className="flex items-center gap-1">Inscription <SortIcon column="enrolledAt" /></span>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-high">
              {filteredLearners.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-14 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gold-container/50 flex items-center justify-center">
                      <Users size={32} className="text-gold" />
                    </div>
                    <h3 className="text-lg font-semibold text-ink mb-1">Aucun apprenant</h3>
                    <p className="text-sm text-ink-soft">Les apprenants s'inscrivent via le LMS.</p>
                  </td>
                </tr>
              ) : (
                filteredLearners.map((learner) => {
                  const isExpanded = expandedLearnerId === learner.id;
                  return (
                    <React.Fragment key={learner.id}>
                      <tr className="hover:bg-cream/50">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleExpand(learner.id)}
                              className="p-0.5 text-ink-soft hover:text-ink transition-colors flex-shrink-0"
                            >
                              <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                            </button>
                            <div className="min-w-0 cursor-pointer" onClick={() => toggleExpand(learner.id)}>
                              <div className="font-semibold text-ink truncate">{learner.fullname}</div>
                              <div className="text-sm text-ink-soft truncate">{learner.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-ink-soft truncate">
                          {learner.groups && learner.groups.length > 0
                            ? learner.groups.map(g => g.name).join(', ')
                            : '-'}
                        </td>
                        <td className="px-4 py-4">
                          <TrackingBadge
                            alert={learnerAlertMap.get(learner.id)?.type}
                            daysSince={learnerAlertMap.get(learner.id)?.daysSince}
                            accountStatus={learner.status}
                            compact
                            wrap
                          />
                        </td>
                        <td className="px-4 py-4">
                          <AccountBadge status={learner.status} />
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-cream-high rounded-full max-w-[80px] overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-700"
                                style={{
                                  width: `${learner.overallProgress}%`,
                                  backgroundColor: learner.overallProgress >= 75 ? MINT : learner.overallProgress >= 50 ? GOLD : CORAL,
                                }}
                              />
                            </div>
                            <span className="text-sm text-ink-soft w-10">{learner.overallProgress}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm">
                          {(() => {
                            const c = completionMap.get(learner.id);
                            const total = c?.total ?? 0;
                            const completed = c?.completed ?? 0;
                            return total === 0
                              ? <span className="text-ink-soft/50">–</span>
                              : <span className="font-medium text-ink">{completed}<span className="text-ink-soft font-normal">/{total}</span></span>;
                          })()}
                        </td>
                        <td className="px-4 py-4 text-sm text-ink-soft">
                          {learner.createdAt ? new Date(learner.createdAt).toLocaleDateString('fr-FR') : '-'}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <button
                            onClick={() => handleViewLearner(learner)}
                            className="p-2 text-ink-soft hover:text-coral hover:bg-coral/10 rounded-lg transition-colors"
                            title="Voir le profil"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} className="px-0 py-0">
                            <div className="bg-cream-high/20 px-10 py-4 border-t border-b border-cream-high">
                              {expandedLoading ? (
                                <div className="flex items-center justify-center py-6">
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-coral" />
                                </div>
                              ) : expandedEnrollments.length === 0 ? (
                                <p className="text-sm text-ink-soft text-center py-4">Aucune formation inscrite</p>
                              ) : (
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-xs text-ink-soft uppercase tracking-[0.05em]">
                                      <th className="text-left py-2">Formation</th>
                                      <th className="text-left py-2">Progression</th>
                                      <th className="text-left py-2">Statut</th>
                                      <th className="text-left py-2">Inscription</th>
                                      <th className="text-left py-2">Completion</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-cream-high">
                                    {expandedEnrollments.map((enr) => (
                                      <tr key={enr.id} className="hover:bg-white/60">
                                        <td className="py-2 font-medium text-ink">{enr.courseName}</td>
                                        <td className="py-2">
                                          <div className="flex items-center gap-2">
                                            <div className="flex-1 h-2 bg-cream-high rounded-full max-w-[80px] overflow-hidden">
                                              <div
                                                className="h-full rounded-full transition-all duration-500"
                                                style={{
                                                  width: `${enr.progressPercent}%`,
                                                  backgroundColor: enr.progressPercent >= 75 ? MINT : enr.progressPercent >= 50 ? GOLD : CORAL,
                                                }}
                                              />
                                            </div>
                                            <span className="text-xs text-ink-soft w-8 text-right">{enr.progressPercent}%</span>
                                          </div>
                                        </td>
                                        <td className="py-2">
                                          <div className="flex items-center gap-1.5">
                                            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                                              enr.status === 'completed' ? 'bg-mint-container text-mint' :
                                              enr.status === 'in_progress' ? 'bg-lavender-container text-lavender' :
                                              'bg-cream-high text-ink-soft'
                                            }`}>
                                              {enr.status === 'completed' ? 'Terminé' : enr.status === 'in_progress' ? 'En cours' : 'Non commencé'}
                                            </span>
                                            {enr.alertType && (
                                              <TrackingBadge alert={enr.alertType} daysSince={enr.alertDays} compact />
                                            )}
                                          </div>
                                        </td>
                                        <td className="py-2 text-ink-soft whitespace-nowrap">
                                          {enr.enrolledAt ? new Date(enr.enrolledAt).toLocaleDateString('fr-FR') : '–'}
                                        </td>
                                        <td className="py-2 text-ink-soft whitespace-nowrap">
                                          {enr.completedAt ? new Date(enr.completedAt).toLocaleDateString('fr-FR') : '–'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {viewingLearner && (
        <LearnerDetailModal
          learner={viewingLearner}
          enrollments={learnerEnrollments}
          loading={loadingEnrollments}
          onClose={() => setViewingLearner(null)}
        />
      )}
    </div>
  );
};

const LearnerDetailModal: React.FC<{
  learner: LearnerWithDetails;
  enrollments: EnrollmentWithProgress[];
  loading: boolean;
  onClose: () => void;
}> = ({ learner, enrollments, loading, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div
      className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-ambient"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-6 border-b border-cream-high">
        <h2 className="text-xl font-semibold text-ink">{learner.fullname}</h2>
        <p className="text-sm text-ink-soft">{learner.email}</p>
        <div className="flex items-center gap-3 mt-3">
          <AccountBadge status={learner.status} />
          {learner.groups && learner.groups.length > 0 && (
            <span className="text-xs text-ink-soft bg-cream-high px-2 py-1 rounded-full">
              {learner.groups.map(g => g.name).join(', ')}
            </span>
          )}
        </div>
      </div>

      <div className="p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {learner.jobTitle && (
            <div>
              <p className="text-xs text-ink-soft">Poste</p>
              <p className="text-sm font-medium text-ink">{learner.jobTitle}</p>
            </div>
          )}
          {learner.department && (
            <div>
              <p className="text-xs text-ink-soft">Departement</p>
              <p className="text-sm font-medium text-ink">{learner.department}</p>
            </div>
          )}
          {learner.enrolledAt && (
            <div>
              <p className="text-xs text-ink-soft">Inscrit le</p>
              <p className="text-sm font-medium text-ink">
                {new Date(learner.enrolledAt).toLocaleDateString('fr-FR')}
              </p>
            </div>
          )}
          {learner.lastSignInAt && (
            <div>
              <p className="text-xs text-ink-soft">Derniere connexion</p>
              <p className="text-sm font-medium text-ink">
                {new Date(learner.lastSignInAt).toLocaleDateString('fr-FR')}
              </p>
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-ink">Progression globale</p>
            <p className="text-sm font-bold text-ink">{learner.overallProgress}%</p>
          </div>
          <div className="h-3 bg-cream-high rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${learner.overallProgress}%`,
                backgroundColor: learner.overallProgress >= 75 ? MINT : learner.overallProgress >= 50 ? GOLD : CORAL,
              }}
            />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-ink mb-3">
            Formations ({enrollments.length})
          </h3>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-coral" />
            </div>
          ) : enrollments.length === 0 ? (
            <p className="text-sm text-ink-soft py-4 text-center">Aucune formation inscrite</p>
          ) : (
            <div className="space-y-3">
              {enrollments.map((e) => (
                <div key={e.id} className="bg-cream-high/20 rounded-xl p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-ink text-sm">{e.course?.title || 'Formation'}</p>
                      <p className="text-xs text-ink-soft">
                        Inscrit le {new Date(e.enrolledAt).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    {e.isCompleted ? (
                      <span className="text-xs bg-mint-container text-mint px-2 py-1 rounded-full">Terminé</span>
                    ) : e.progressPercent > 0 ? (
                      <span className="text-xs bg-lavender-container text-lavender px-2 py-1 rounded-full">En cours</span>
                    ) : (
                      <span className="text-xs bg-cream-high text-ink-soft px-2 py-1 rounded-full">Non commencé</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-cream-high rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${e.progressPercent}%`,
                          backgroundColor: e.progressPercent >= 75 ? MINT : e.progressPercent >= 50 ? GOLD : CORAL,
                        }}
                      />
                    </div>
                    <span className="text-xs text-ink-soft w-8">{e.progressPercent}%</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="p-6 border-t border-cream-high flex justify-end">
        <button onClick={onClose} className="btn-secondary">Fermer</button>
      </div>
    </div>
  </div>
);
